import { Link } from "react-router-dom";
import { ArrowRight, Check, Star, Shield, Clock, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SEOPage = ({ location, service }) => {
  const serviceKey = service.toLowerCase().split(" ")[0]; // bathroom, kitchen, floor
  
  const serviceData = {
    bathroom: {
      title: `Professional ${service} in ${location}`,
      description: `Transform your bathroom with expert tiling services in ${location}. From modern wet rooms to classic bathroom renovations, DC Tiling delivers exceptional results.`,
      image: "https://images.unsplash.com/photo-1758448018619-4cbe2250b9ad?w=800",
      features: [
        "Complete bathroom tiling",
        "Wet room installations",
        "Shower enclosures",
        "Waterproof membranes",
        "Underfloor heating compatible"
      ],
      faqs: [
        { q: "How long does bathroom tiling take?", a: "A standard bathroom typically takes 3-5 days, depending on size and complexity." },
        { q: "Do you supply tiles?", a: "We can recommend and source tiles, or work with tiles you've already chosen." },
        { q: "Is the bathroom waterproofed?", a: "Yes, we apply waterproof membranes to all wet areas before tiling." }
      ]
    },
    kitchen: {
      title: `Professional ${service} in ${location}`,
      description: `Enhance your kitchen with stunning tiled splashbacks and floors in ${location}. Expert installation for a beautiful, durable finish.`,
      image: "https://images.unsplash.com/photo-1761679296778-7f245d39148d?w=800",
      features: [
        "Splashback installations",
        "Kitchen floor tiling",
        "Island surrounds",
        "Heat-resistant materials",
        "Easy-clean finishes"
      ],
      faqs: [
        { q: "Can you tile around cookers?", a: "Yes, we use heat-resistant materials suitable for all kitchen installations." },
        { q: "How do I maintain kitchen tiles?", a: "Kitchen tiles are easy to maintain with regular cleaning. We recommend sealed grout for easy upkeep." },
        { q: "Can you tile over existing tiles?", a: "In some cases yes, but we'll assess this during your quote inspection." }
      ]
    },
    floor: {
      title: `Professional ${service} in ${location}`,
      description: `Quality floor tiling throughout ${location}. From hallways to living spaces, we install durable, beautiful floor tiles that last.`,
      image: "https://images.unsplash.com/photo-1695191388218-f6259600223f?w=800",
      features: [
        "Hallway floor tiling",
        "Living area floors",
        "Conservatory tiling",
        "Utility room floors",
        "Underfloor heating"
      ],
      faqs: [
        { q: "Do you prepare the subfloor?", a: "Yes, proper subfloor preparation is included to ensure a perfect finish." },
        { q: "Can you install underfloor heating?", a: "We work with underfloor heating systems and can tile over them." },
        { q: "What tile sizes do you work with?", a: "We handle all sizes from mosaics to large format tiles." }
      ]
    }
  };

  const data = serviceData[serviceKey] || serviceData.bathroom;

  const trustItems = [
    { icon: Star, value: "5.0", label: "Google Rating" },
    { icon: Shield, value: "Fully", label: "Insured" },
    { icon: Clock, value: "Free", label: "Quotations" },
    { icon: Award, value: "15+", label: "Years Experience" }
  ];

  return (
    <div data-testid={`seo-page-${serviceKey}-${location.toLowerCase()}`}>
      {/* Hero */}
      <section className="seo-hero">
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-primary-foreground/80 mb-4 uppercase tracking-wider text-sm">
                {location} Tiling Specialists
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat">
                {data.title}
              </h1>
              <p className="text-lg text-white/80 mb-8">
                {data.description}
              </p>
              <Link to="/quote">
                <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="seo-cta-primary">
                  Get Your Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="hidden lg:block">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustItems.map((item, index) => (
              <div key={index} className="trust-item">
                <div className="trust-icon">
                  <item.icon className="w-6 h-6" />
                </div>
                <p className="font-bold text-2xl text-gray-900">{item.value}</p>
                <p className="text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-montserrat">
                What We Offer in {location}
              </h2>
              <ul className="space-y-4 mb-8">
                {data.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-lg">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/quote">
                <Button className="bg-primary hover:bg-primary/90" data-testid="seo-features-cta">
                  Check My Project
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="aspect-[4/3] rounded-xl overflow-hidden">
              <img src={data.image} alt={`${service} in ${location}`} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center font-montserrat">
            Frequently Asked Questions
          </h2>
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible>
              {data.faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-medium">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Ready for {service} in {location}?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Get your free, no-obligation quote today. We serve all of {location} and surrounding areas in Greater Manchester.
          </p>
          <Link to="/quote">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="seo-final-cta">
              Start My Tile Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SEOPage;
